/**
 * Shared label helpers for `<InterruptedResumePanel>` (the single recovery UI,
 * rendered on both the patient chart and the `/call` route). Typed structurally
 * so both `ActiveScribeSession` and the provider's `InterruptedSession` satisfy
 * them.
 */
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

export interface CapturedSessionLike {
  startedAt: string
  lastChunkAt?: string
  chunkCount: number
}

/**
 * Captured-duration label between `startedAt` and `lastChunkAt` (or now).
 *
 * Short interrupted calls (< 1 min) used to round to "0 minutes", which read
 * as nonsense next to a non-zero transcript line count. When any transcript
 * was captured we instead show whole seconds under a minute, and clamp to at
 * least 1 minute once we're over the minute mark.
 */
export function capturedDurationLabel(session: CapturedSessionLike): string {
  const start = dayjs(session.startedAt)
  const end = session.lastChunkAt ? dayjs(session.lastChunkAt) : dayjs()
  const totalSeconds = Math.max(0, Math.round(end.diff(start, 'second', true)))

  // Under a minute but with captured lines — show seconds so the chip isn't a
  // confusing "0 minutes" sitting next to "N lines".
  if (totalSeconds < 60 && session.chunkCount > 0) {
    const secs = Math.max(1, totalSeconds)
    return `${secs} ${secs === 1 ? 'second' : 'seconds'}`
  }

  const mins = Math.max(session.chunkCount > 0 ? 1 : 0, Math.round(totalSeconds / 60))
  return `${mins} ${mins === 1 ? 'minute' : 'minutes'}`
}

export function lastActivityLabel(session: CapturedSessionLike): string {
  const iso = session.lastChunkAt ?? session.startedAt
  return dayjs(iso).fromNow()
}
